import type { Session, SupabaseClient, User } from '@supabase/supabase-js';

export interface TrustedPasswordLoginResult {
  data: {
    session: Session | null;
    user: User | null;
  };
  error: Error | null;
}

class TrustedPasswordLoginError extends Error {
  readonly code?: string;
  readonly status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'TrustedPasswordLoginError';
    this.code = code;
    this.status = status;
  }
}

async function invocationError(error: unknown): Promise<Error> {
  const fallback = error instanceof Error ? error.message : 'Unable to sign in';
  const context = (error as { context?: unknown } | null)?.context;
  if (typeof Response !== 'undefined' && context instanceof Response) {
    try {
      const payload = await context.json() as Record<string, unknown>;
      return new TrustedPasswordLoginError(
        typeof payload.error === 'string' ? payload.error : fallback,
        typeof payload.code === 'string' ? payload.code : undefined,
        context.status,
      );
    } catch {
      return new TrustedPasswordLoginError(fallback, undefined, context.status);
    }
  }
  return new TrustedPasswordLoginError(fallback);
}

/**
 * Routes every AX1 password verification through the trusted Edge boundary.
 * Supabase Auth remains the credential authority; this helper only installs a
 * successful returned session in the normal browser client.
 */
export async function signInWithTrustedPasswordTelemetry(
  client: SupabaseClient,
  email: string,
  password: string,
): Promise<TrustedPasswordLoginResult> {
  const { data, error } = await client.functions.invoke<Record<string, unknown>>(
    'security-password-login',
    { body: { email, password } },
  );

  if (error) {
    return {
      data: { session: null, user: null },
      error: await invocationError(error),
    };
  }

  const accessToken = typeof data?.access_token === 'string' ? data.access_token : '';
  const refreshToken = typeof data?.refresh_token === 'string' ? data.refresh_token : '';
  if (!accessToken || !refreshToken) {
    return {
      data: { session: null, user: null },
      error: new TrustedPasswordLoginError(
        typeof data?.error === 'string'
          ? data.error
          : 'Authentication service returned an invalid session',
        typeof data?.code === 'string' ? data.code : undefined,
      ),
    };
  }

  const { data: sessionData, error: sessionError } = await client.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken,
  });
  return {
    data: {
      session: sessionData.session,
      user: sessionData.user,
    },
    error: sessionError,
  };
}
